import React from 'react';
import { Calendar, ClipboardCheck, GitBranch, BarChart3, Settings, Users2, FileCheck, Workflow } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const ServicesSection = () => {
  const services = [
    {
      icon: Calendar,
      title: 'Project Planning',
      description: 'Comprehensive planning including scope definition, timeline development, resource allocation, and risk assessment.',
      color: 'from-[#8B2635] to-[#A63446]',
    },
    {
      icon: Users2,
      title: 'Team Management',
      description: 'Building and leading high-performing teams with clear roles, responsibilities, and collaborative workflows.',
      color: 'from-[#A63446] to-[#C14758]',
    },
    {
      icon: Workflow,
      title: 'Process Development',
      description: 'Designing efficient workflows, standardizing procedures, and implementing best practices for optimal execution.',
      color: 'from-[#8B2635] to-[#982D40]',
    },
    {
      icon: Settings,
      title: 'System Integration',
      description: 'Seamlessly integrating project management tools, software, and methodologies into existing infrastructure.',
      color: 'from-[#A63446] to-[#8B2635]',
    },
    {
      icon: BarChart3,
      title: 'Performance Tracking',
      description: 'Real-time monitoring, analytics, and reporting to ensure projects stay on track and meet objectives.',
      color: 'from-[#982D40] to-[#A63446]',
    },
    {
      icon: GitBranch,
      title: 'Change Management',
      description: 'Managing project changes effectively with controlled processes and stakeholder communication.',
      color: 'from-[#C14758] to-[#8B2635]',
    },
    {
      icon: ClipboardCheck,
      title: 'Quality Assurance',
      description: 'Rigorous quality control measures, testing protocols, and validation to ensure deliverable excellence.',
      color: 'from-[#8B2635] to-[#C14758]',
    },
    {
      icon: FileCheck,
      title: 'Project Closure',
      description: 'Formal project completion, documentation, lessons learned, and successful handover to stakeholders.',
      color: 'from-[#A63446] to-[#982D40]',
    },
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-br from-[#F5F1E8] via-white to-[#FFF8F0] relative overflow-hidden">
      {/* Floating Background Elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-[#8B2635]/5 to-transparent rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-[#D4A574]/5 to-transparent rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-[#8B2635]">Services</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#8B2635] to-[#A63446] mx-auto mb-8" />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive project management solutions tailored to your organization's unique needs and objectives.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-0 bg-white overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-8 relative h-full flex flex-col">
                  {/* Gradient Background */}
                  <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${service.color} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />
                  
                  {/* Icon */}
                  <div className="relative z-10 mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#8B2635] transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed flex-1">{service.description}</p>
                  </div>

                  {/* Hover Effect Element */}
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-[#8B2635]/5 to-transparent rounded-tl-full transform translate-x-12 translate-y-12 group-hover:scale-150 transition-transform duration-500" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;